import HyperHTMLElement from 'hyperhtml-element';
import Menu from './menu';

const OPENED_CLASS_NAME = 'aras-dropdown_opened';

const getDropdownSize = function (element: HTMLElement) {
	element.style.visibility = 'hidden';
	const size = {
		width: element.offsetWidth,
		height: element.offsetHeight
	};
	element.style.visibility = '';

	return size;
};

const udpatePositionOfDropdownBoxElement = (
	element: HTMLElement,
	dropdownPosition: string
) => {
	if (element instanceof Menu) {
		let horizontalAlignmentSide = null;
		if (dropdownPosition.endsWith('left')) {
			horizontalAlignmentSide = 'left';
		} else if (dropdownPosition.endsWith('right')) {
			horizontalAlignmentSide = 'right';
		}

		element.calcSubmenuPosition(element, {
			preventGreedyVerticalExpansion: true,
			horizontalAlignmentSide
		});
	}
};

const updatePositionOfDroprownBox = function (
	parentProps: { width: number; height: number; offsetTop: number },
	dropdownBox: HTMLElement
) {
	const elmRect = dropdownBox.getBoundingClientRect();
	const elementBottom = dropdownBox.offsetHeight + parentProps.offsetTop;
	const docElement = document.documentElement;
	const docSize = {
		width: docElement.clientWidth,
		height: docElement.clientHeight
	};

	if (elmRect.right > docSize.width) {
		dropdownBox.style.left =
			parseInt(dropdownBox.style.left) - (elmRect.right - docSize.width) + 'px';
	}
	if (elementBottom > parentProps.height) {
		dropdownBox.style.top =
			parseInt(dropdownBox.style.top) -
			(elementBottom - parentProps.height) +
			'px';
	}
	if (elmRect.left < 0) {
		dropdownBox.style.left =
			parseInt(dropdownBox.style.left) + Math.abs(elmRect.left) + 'px';
	}
	if (parentProps.offsetTop < 0) {
		dropdownBox.style.top =
			parseInt(dropdownBox.style.top) + Math.abs(parentProps.offsetTop) + 'px';
	}
};

const getSourcePosition = function (
	pos: string,
	dropdownSize: { width: number; height: number },
	buttonSize: { width: number; height: number }
) {
	switch (pos) {
		case 'bottom-left':
			return { top: 0 + buttonSize.height, left: 0 };
		case 'bottom-right':
			return {
				top: 0 + buttonSize.height,
				left: 0 + buttonSize.width - dropdownSize.width
			};
		case 'bottom-center':
			return {
				top: 0 + buttonSize.height,
				left: 0 + buttonSize.width / 2 - dropdownSize.width / 2
			};
		case 'top-left':
			return { top: 0 - dropdownSize.height, left: 0 };
		case 'top-right':
			return {
				top: 0 - dropdownSize.height,
				left: 0 + buttonSize.width - dropdownSize.width
			};
		case 'top-center':
			return {
				top: 0 - dropdownSize.height,
				left: 0 + buttonSize.width / 2 - dropdownSize.width / 2
			};
		case 'left-top':
			return { top: 0, left: 0 - dropdownSize.width };
		case 'left-bottom':
			return {
				top: 0 + buttonSize.height - dropdownSize.height,
				left: 0 - dropdownSize.width
			};
		case 'left-center':
			return {
				top: 0 + buttonSize.height / 2 - dropdownSize.height / 2,
				left: 0 - dropdownSize.width
			};
		case 'right-top':
			return { top: 0, left: 0 + buttonSize.width };
		case 'right-bottom':
			return {
				top: 0 + buttonSize.height - dropdownSize.height,
				left: 0 + buttonSize.width
			};
		case 'right-center':
			return {
				top: 0 + buttonSize.height / 2 - dropdownSize.height / 2,
				left: 0 + buttonSize.width
			};
		// the same as bottom-left
		default:
			return { top: 0 + buttonSize.height, left: 0 };
	}
};

const getOffsetParentProps = function (
	container: HTMLElement | Dropdown,
	offsetTop: number
): { width: number; height: number; offsetTop: number } {
	offsetTop += container.offsetTop;
	const offsetParent = container.offsetParent;
	if (!(offsetParent instanceof HTMLElement)) {
		const docElement = document.documentElement;
		return {
			offsetTop: offsetTop,
			width: docElement.clientWidth,
			height: docElement.clientHeight
		};
	}

	const elementOverflow = getComputedStyle(offsetParent).overflow;
	if (elementOverflow === 'hidden') {
		return {
			offsetTop: offsetTop,
			width: offsetParent.clientWidth,
			height: offsetParent.clientHeight
		};
	}

	return getOffsetParentProps(offsetParent, offsetTop);
};

export default class Dropdown extends HyperHTMLElement {
	private declare buttonNode: HTMLElement;
	private declare dropdownBox: HTMLElement;
	private declare buttonSize: { width: number; height: number };
	private initialized = false;

	private blurEventHandler = ({ relatedTarget }: FocusEvent) => {
		const isDropdownContentBecameFocused =
			(relatedTarget instanceof Node &&
				this.dropdownBox.contains(relatedTarget)) ||
			this.dropdownBox.contains(document.activeElement);
		if (!isDropdownContentBecameFocused) {
			this.closeDropdown();
		}
	};

	connectedCallback(): void {
		if (this.initialized) {
			return;
		}

		this.addEventListener('click', (e) => this.handleDropdownContentClick(e));
		this.addEventListener('keydown', (e) => this.handleKeydown(e));
		this.addEventListener('click', (e) => this.toggleDropdown(e));
		// Using 'mousedown' event to prevent open dropdown if it was opened before
		this.addEventListener('mousedown', (e) => this.mouseDown(e));
		this.initialized = true;
	}
	private recalculateButtonSize() {
		if (this.buttonNode) {
			this.buttonSize = {
				width: this.buttonNode.offsetWidth,
				height: this.buttonNode.offsetHeight
			};
		}
	}
	private convertEventToDropbox(target: EventTarget | null) {
		if (!(target instanceof Element)) {
			return;
		}
		const targetButton = target.closest<HTMLElement>('[dropdown-button]');
		const container = targetButton?.closest<Dropdown>('aras-dropdown');
		const dropdownBox = container?.querySelector<HTMLElement>('.aras-dropdown');
		if (
			!targetButton ||
			!dropdownBox ||
			container !== this ||
			targetButton.hasAttribute('disabled') ||
			targetButton.getAttribute('aria-disabled') === 'true'
		) {
			return;
		}

		if (!this.dropdownBox || this.dropdownBox !== dropdownBox) {
			this.dropdownBox = dropdownBox;
			this.dropdownBox.tabIndex = 0;
		}
		if (!this.buttonNode || this.buttonNode !== targetButton) {
			this.buttonNode = targetButton;
			this.recalculateButtonSize();
		}

		return dropdownBox;
	}

	private mouseDown(e: MouseEvent) {
		const dropdownBox = this.convertEventToDropbox(e.target);
		if (!dropdownBox) {
			return;
		}
		if (this.dropdownBox.classList.contains(OPENED_CLASS_NAME)) {
			e.preventDefault();
		}
	}

	private toggleDropdown({ target }: Event) {
		const dropdownBox = this.convertEventToDropbox(target);
		if (!dropdownBox) {
			return;
		}
		const parentProps = getOffsetParentProps(this, 0);
		this.dropdownBox.style.maxHeight = 'none';
		this.dropdownBox.style.overflowY = 'visible';
		this.dropdownBox.classList.toggle(OPENED_CLASS_NAME);

		if (this.dropdownBox.classList.contains(OPENED_CLASS_NAME)) {
			this.dispatchCustomEvent('dropdownbeforeopen');
			const dropdownSize = getDropdownSize(this.dropdownBox);
			const pos = this.getAttribute('position') || 'bottom-left';
			const sourcePosition = getSourcePosition(
				pos,
				dropdownSize,
				this.buttonSize
			);
			const leftPos = sourcePosition.left + 'px';
			const topPos = sourcePosition.top + 'px';
			if (parentProps.height < this.dropdownBox.clientHeight) {
				this.dropdownBox.style.maxHeight = parentProps.height + 'px';
				this.dropdownBox.style.overflowY = 'auto';
			}
			if (
				this.dropdownBox.style.left !== leftPos ||
				this.dropdownBox.style.top !== topPos
			) {
				this.dropdownBox.style.left = leftPos;
				this.dropdownBox.style.top = topPos;
			}
			parentProps.offsetTop += this.dropdownBox.offsetTop;
			updatePositionOfDroprownBox(parentProps, this.dropdownBox);
			udpatePositionOfDropdownBoxElement(this.dropdownBox, pos);

			this.dropdownBox.addEventListener('blur', this.blurEventHandler, true);
			this.dispatchCustomEvent('dropdownopened');
			this.setAttribute('open', '');
			this.buttonNode.setAttribute('aria-expanded', 'true');
			this.dropdownBox.focus();
		} else {
			this.removeAttribute('open');
			this.buttonNode.focus();
		}
	}
	private dispatchCustomEvent(type: string) {
		const evt = new CustomEvent(type, { bubbles: true, cancelable: true });
		this.dropdownBox.dispatchEvent(evt);
	}

	private closeDropdown() {
		this.dropdownBox.removeEventListener('blur', this.blurEventHandler, true);
		this.dropdownBox.classList.remove(OPENED_CLASS_NAME);
		this.dropdownBox.blur();
		this.dispatchCustomEvent('dropdownclosed');
		this.buttonNode.removeAttribute('aria-expanded');
		this.removeAttribute('open');
		document.body.focus();
	}

	private handleDropdownContentClick({ target }: MouseEvent) {
		if (
			!(target instanceof Element) ||
			target.closest('[dropdown-button]') ||
			target === this
		) {
			return;
		}

		const isContainerClicked = target === this.dropdownBox;

		const listNode = target.closest('li[data-index]');
		const isParentListNodeClicked =
			listNode && listNode.classList.contains('aras-list__parent');

		if (
			this.hasAttribute('closeonclick') &&
			!isContainerClicked &&
			!isParentListNodeClicked
		) {
			this.closeDropdown();
		}
	}
	private handleKeydown(e: KeyboardEvent) {
		switch (e.code) {
			case 'Escape':
			case e.altKey && 'ArrowUp':
				if (this.hasAttribute('open')) {
					this.closeDropdown();
					this.buttonNode.focus();
				}
				break;
			case 'Enter':
			case 'NumpadEnter':
			case 'Space':
			case e.altKey && 'ArrowDown':
				if (
					e.target instanceof Element &&
					e.target.closest('[dropdown-button]') &&
					!this.hasAttribute('open')
				) {
					this.toggleDropdown(e);
					// :active state doesn't remove from button https://bugs.chromium.org/p/chromium/issues/detail?id=973035&can=2&q=973035
					e.preventDefault();
				}
				break;
		}
	}
}
